import { UserCheck } from "lucide-react";
import { assignmentActionsFor } from "@school-sales-ai/shared";
import {
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu";
import { AssigneeMenuItems } from "@/components/lead-assignment/AssigneeMenuItems";
import { hasAssignmentActions } from "@/lib/lead-assignment";
import { useChangeAssignee, useLeadAssignmentPolicy } from "@/hooks/useLeadAssignment";
import { useCurrentUserProfile } from "@/hooks/useUsers";

/**
 * «Asignar a…» inside a board card's «…» menu — the lead page's control as a
 * submenu. Rendered inside the menu's content, so its hooks only mount once the
 * menu is open, never once per card on the board.
 *
 * No `onLeftScope` here: a lead a member hands away simply drops off the board
 * when the pipeline refetches.
 */
export function AssigneeSubMenu({
  leadId,
  assignedProfileId,
}: {
  leadId: string;
  assignedProfileId: string | null;
}): React.JSX.Element | null {
  const { data: policy } = useLeadAssignmentPolicy();
  const profile = useCurrentUserProfile();
  const change = useChangeAssignee(leadId);

  if (!policy?.enabled || !profile) return null;
  const actions = assignmentActionsFor(policy, profile, assignedProfileId);
  if (!hasAssignmentActions(actions)) return null;

  return (
    <>
      <DropdownMenuSub>
        <DropdownMenuSubTrigger>
          <UserCheck className="h-3.5 w-3.5" />
          Asignar a…
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent className="w-60">
          <AssigneeMenuItems
            actions={actions}
            members={policy.members}
            viewerId={profile.id}
            assignedProfileId={assignedProfileId}
            onSelect={(profileId) => {
              // Re-picking the current owner would only earn a 409 toast.
              if (profileId !== assignedProfileId) change.mutate(profileId);
            }}
          />
        </DropdownMenuSubContent>
      </DropdownMenuSub>
      <DropdownMenuSeparator />
    </>
  );
}
